import * as i0 from '@angular/core';
import { forwardRef, Directive, Optional, Inject, Injectable, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, DefaultValueAccessor, COMPOSITION_BUFFER_MODE, FormsModule } from '@angular/forms';
import { EVENT_MANAGER_PLUGINS } from '@angular/platform-browser';

const OUTSIDE_ZONE_DEFAULT_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    useExisting: forwardRef(() => OutsideZoneDefaultValueAccessor),
    multi: true,
};
class OutsideZoneDefaultValueAccessor {
    constructor(renderer, el, mode) {
        this.accessor = new DefaultValueAccessor(renderer, el, mode);
    }
    writeValue(value) {
        this.accessor.writeValue(value);
    }
    registerOnChange(fn) {
        this.accessor.registerOnChange(fn);
    }
    registerOnTouched(fn) {
        this.accessor.registerOnTouched(fn);
    }
    setDisabledState(isDisabled) {
        this.accessor.setDisabledState(isDisabled);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: OutsideZoneDefaultValueAccessor, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: COMPOSITION_BUFFER_MODE, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.10", type: OutsideZoneDefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName][outside-zone],textarea[formControlName][outside-zone],input:not([type=checkbox])[formControl][outside-zone],textarea[formControl][outside-zone],input:not([type=checkbox])[ngModel][outside-zone],textarea[ngModel][outside-zone],[ngDefaultControl][outside-zone]", host: { listeners: { "input.outside-zone": "$any(this.accessor)._handleInput($event.target.value)", "blur.outside-zone": "$any(this.accessor).onTouched()", "compositionstart.outside-zone": "$any(this.accessor)._compositionStart()", "compositionend.outside-zone": "$any(this.accessor)._compositionEnd($event.target.value)" } }, providers: [OUTSIDE_ZONE_DEFAULT_VALUE_ACCESSOR], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: OutsideZoneDefaultValueAccessor, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input:not([type=checkbox])[formControlName][outside-zone],textarea[formControlName][outside-zone],input:not([type=checkbox])[formControl][outside-zone],textarea[formControl][outside-zone],input:not([type=checkbox])[ngModel][outside-zone],textarea[ngModel][outside-zone],[ngDefaultControl][outside-zone]', // tslint:disable-line max-line-length directive-selector
                    host: {
                        // tslint:disable-line use-host-property-decorator
                        '(input.outside-zone)': '$any(this.accessor)._handleInput($event.target.value)',
                        '(blur.outside-zone)': '$any(this.accessor).onTouched()',
                        '(compositionstart.outside-zone)': '$any(this.accessor)._compositionStart()',
                        '(compositionend.outside-zone)': '$any(this.accessor)._compositionEnd($event.target.value)',
                    },
                    providers: [OUTSIDE_ZONE_DEFAULT_VALUE_ACCESSOR],
                }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [COMPOSITION_BUFFER_MODE]
                }] }] });

function parseEventName(name) {
    return name.split('.').shift() || '';
}
function parseGlobalTarget(name) {
    if (name === 'window')
        return window;
    if (name === 'document')
        return document;
    if (name === 'body')
        return document.body;
    throw new Error('Element selector [${name}] not supported by out zone plugin.');
}
function listen(target, name, handler, zone) {
    zone.runOutsideAngular(() => target.addEventListener(name, handler, false));
    return () => target.removeEventListener(name, handler, false);
}
class OutsideZonePlugin {
    supports(name) {
        return name.includes('.outside-zone');
    }
    addEventListener(el, name, handler) {
        const event = parseEventName(name);
        return listen(el, event, handler, this.manager.getZone());
    }
    addGlobalEventListener(el, name, handler) {
        const target = parseGlobalTarget(el);
        const event = parseEventName(name);
        return listen(target, event, handler, this.manager.getZone());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: OutsideZonePlugin, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: OutsideZonePlugin }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: OutsideZonePlugin, decorators: [{
            type: Injectable
        }] });

const OUTSIDE_ZONE_EVENT_PLUGIN = {
    provide: EVENT_MANAGER_PLUGINS,
    useClass: OutsideZonePlugin,
    multi: true,
};

class OutsideZoneModule {
    static forRoot() {
        return {
            ngModule: OutsideZoneModule,
            providers: [OUTSIDE_ZONE_EVENT_PLUGIN],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: OutsideZoneModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.10", ngImport: i0, type: OutsideZoneModule, declarations: [OutsideZoneDefaultValueAccessor], imports: [FormsModule], exports: [OutsideZoneDefaultValueAccessor] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: OutsideZoneModule, imports: [FormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: OutsideZoneModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [FormsModule],
                    exports: [OutsideZoneDefaultValueAccessor],
                    declarations: [OutsideZoneDefaultValueAccessor],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { OUTSIDE_ZONE_DEFAULT_VALUE_ACCESSOR, OUTSIDE_ZONE_EVENT_PLUGIN, OutsideZoneDefaultValueAccessor, OutsideZoneModule, OutsideZonePlugin };
